import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Collapse,
    IconButton
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    TrendingFlat as ArrowIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { InvoiceService } from '../../../../../service/invoiceService';
import { toast } from 'react-toastify';

/**
 * InvoiceTransferTrail Component
 * 
 * Displays the complete transfer history of an invoice, showing the chain
 * of transfers from the original invoice to the current one.
 * 
 * Features:
 * - Collapsible interface to save space
 * - Visual chain showing transfer direction
 * - Clickable invoice numbers to navigate between invoices
 * - Color-coded status indicators
 * - Handles both forward (transferred to) and backward (transferred from) chains
 * 
 * @param {Object} props
 * @param {Object} props.invoiceData - Current invoice data containing transfer relationships
 * @param {Function} props.onInvoiceSelect - Callback when user clicks on an invoice number in the trail
 */
const InvoiceTransferTrail = ({ invoiceData, onInvoiceSelect }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [transferChain, setTransferChain] = useState([]);
    const [transferEvents, setTransferEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    /**
     * Load transfer chain when component mounts or invoice changes
     */
    useEffect(() => {
        /**
         * Build the complete transfer chain by following both directions
         * This creates a chronological view of all transfers in the chain
         */
        const buildTransferChain = async () => {
            setLoading(true);
            try {
                const resp = await InvoiceService.getTransferEvents(invoiceData.id);
                if (resp.code === 0) {
                    const events = resp.data || [];
                    setTransferEvents(events);
                    // derive chain from from->to invoices in events
                    const nodes = new Map();
                    events.forEach(e => {
                        if (e.fromInvoice) nodes.set(e.fromInvoice.id, e.fromInvoice);
                        if (e.toInvoice) nodes.set(e.toInvoice.id, e.toInvoice);
                    });
                    // try to order by relation following events
                    const ordered = Array.from(nodes.values());
                    // seed with current if present
                    const current = nodes.get(invoiceData.id) || null;
                    if (current) {
                        // move current to front
                        const idx = ordered.findIndex(x => x.id === current.id);
                        if (idx > 0) {
                            ordered.splice(idx, 1);
                            ordered.unshift(current);
                        }
                    }
                    setTransferChain(ordered);
                } else {
                    setTransferChain([]);
                }
            } catch (error) {
                console.error('Error loading transfer events:', error);
                toast.error('Failed to load transfer history');
            } finally {
                setLoading(false);
            }
        };

        buildTransferChain();
    }, [invoiceData]);

    /**
     * Handle clicking on an invoice number in the trail
     */
    const handleInvoiceClick = (invoice) => {
        if (onInvoiceSelect) {
            onInvoiceSelect(invoice);
        }
    };

    /**
     * Get appropriate color for invoice status
     */
    const getStatusColor = (status) => {
        switch (status) {
            case 'transferred': return 'warning';
            case 'completed': return 'success';
            case 'in progress': return 'info';
            case 'cancelled': return 'error';
            case 'voided': return 'error';
            default: return 'default';
        }
    };

    // Don't render if no transfer relationships exist
    if (!invoiceData.transferredFrom && !invoiceData.transferredTo) {
        return null;
    }

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 2, 
                mb: 2, 
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef'
            }}
        >
            {/* Header with expand/collapse toggle */}
            <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                sx={{ cursor: 'pointer' }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <HistoryIcon color="primary" />
                    <Typography variant="h6" color="primary">
                        Transfer History
                    </Typography>
                    <Chip 
                        label={`${transferChain.length} invoices`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                    />
                </Box>
                <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* Collapsible transfer chain */}
            <Collapse in={isExpanded}>
                <Box sx={{ mt: 2 }}>
                    {loading ? (
                        <Typography variant="body2" color="text.secondary">
                            Loading transfer history...
                        </Typography>
                    ) : transferChain.length > 0 ? (
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                            {transferChain.map((invoice, index) => (
                                <Box 
                                    key={invoice.id} 
                                    display="flex" 
                                    alignItems="center" 
                                    gap={1}
                                >
                                    {/* Invoice chip */}
                                    <Chip
                                        label={
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {invoice.invoiceNumber}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ({invoice.status})
                                                </Typography>
                                            </Box>
                                        }
                                        color={invoice.id === invoiceData.id ? 'primary' : getStatusColor(invoice.status)}
                                        variant={invoice.id === invoiceData.id ? 'filled' : 'outlined'}
                                        clickable={true}
                                        onClick={() => handleInvoiceClick(invoice)}
                                        sx={{
                                            fontSize: '0.875rem',
                                            height: 'auto',
                                            '& .MuiChip-label': { 
                                                px: 1.5, 
                                                py: 0.5 
                                            },
                                            // Highlight current invoice
                                            ...(invoice.id === invoiceData.id && {
                                                fontWeight: 'bold',
                                                boxShadow: 2
                                            })
                                        }}
                                    />

                                    {/* Arrow between invoices (except for last item) */}
                                    {index < transferChain.length - 1 && (
                                        <ArrowIcon 
                                            color="action" 
                                            sx={{ fontSize: 16 }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No transfer history available
                        </Typography>
                    )}

                    {/* Reason chips for the latest event involving this invoice */}
                    {!loading && transferEvents.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {transferEvents[transferEvents.length - 1].reasons?.map(r => (
                                <Chip key={r.id} size="small" color="default" label={r.description || r.code} />
                            ))}
                        </Box>
                    )}

                    {/* Additional info */}
                    {transferChain.length > 0 && (
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ mt: 1, display: 'block' }}
                        >
                            Click on any invoice number to view its details. 
                            Current invoice is highlighted in blue.
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default InvoiceTransferTrail;
